/**
 * Avatar circle showing 2-letter initials. The background colour is
 * deterministically picked from a small palette per pair of initials
 * (matches the maquettes' avatar-MT / -CR / -HR / -CF and degrades
 * gracefully for any other pair).
 */

const PALETTE: { match: RegExp; bg: string }[] = [
  { match: /^MT$/i, bg: '#5B6BB6' },
  { match: /^CR$/i, bg: '#B58B5B' },
  { match: /^HR$/i, bg: '#6B8478' },
  { match: /^CF$/i, bg: '#8B6B9E' },
];

const FALLBACKS = ['#5B6BB6', '#B58B5B', '#6B8478', '#8B6B9E', '#7A6BB6', '#9E7A6B'];

function colourFor(initials: string): string {
  const norm = initials.toUpperCase();
  for (const { match, bg } of PALETTE) if (match.test(norm)) return bg;
  // Stable fallback by char-sum modulo palette length
  let hash = 0;
  for (const c of norm) hash = (hash * 31 + c.charCodeAt(0)) % 1024;
  return FALLBACKS[hash % FALLBACKS.length]!;
}

export function toInitials(name: string | null | undefined): string {
  if (!name) return '··';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

type Props = {
  initials: string;
  size?: 'sm' | 'md';
  title?: string;
  className?: string;
};

export default function Avatar({ initials, size = 'md', title, className = '' }: Props) {
  const dim = size === 'sm' ? 'w-[22px] h-[22px] text-[9px]' : 'w-[28px] h-[28px] text-[10px]';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold text-text-on-dark border-2 border-paper-elevated tracking-[0.3px] ${dim} ${className}`}
      style={{ background: colourFor(initials) }}
      title={title ?? initials}
      aria-label={title ?? initials}
    >
      {initials}
    </span>
  );
}

export function AvatarStack({
  items,
  size = 'sm',
  className = '',
}: {
  items: { initials: string; title?: string }[];
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <span className={`inline-flex ${className}`}>
      {items.map((it, i) => {
        const props: { initials: string; size: 'sm' | 'md'; title?: string; className?: string } = {
          initials: it.initials,
          size,
        };
        if (it.title !== undefined) props.title = it.title;
        if (i > 0) props.className = '-ml-2';
        return <Avatar key={`${it.initials}-${i}`} {...props} />;
      })}
    </span>
  );
}
